import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Fixed-position dropdown anchored to a toolbar control (escapes overflow:hidden parents).
 * @param {"left"|"center"|"right"} align
 * @param {React.RefObject} [boundsRef] — clamp menu inside this element (e.g. toolbar)
 */
const TextFormatFloatingMenu = ({
  open = false,
  anchorRef,
  onClose = () => {},
  children,
  className = "",
  align = "left",
  offsetY = 6,
  ignoreCloseSelectors = [],
  style = null,
  boundsRef = null,
}) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const shouldIgnoreClose = useCallback(
    (target) => {
      if (!target) return false;
      if (anchorRef?.current?.contains(target)) return true;
      if (menuRef.current?.contains(target)) return true;
      for (const sel of ignoreCloseSelectors) {
        if (typeof sel === "string" && target.closest?.(sel)) return true;
      }
      return false;
    },
    [anchorRef, ignoreCloseSelectors]
  );

  const placeMenu = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth || 200;
    const menuHeight = menuEl?.offsetHeight || 0;
    let x = rect.left;
    if (align === "center") {
      x = rect.left + rect.width / 2 - menuWidth / 2;
    } else if (align === "right") {
      x = rect.right - menuWidth;
    }
    let y = rect.bottom + offsetY;
    if (menuHeight > 0 && y + menuHeight > window.innerHeight - 12) {
      y = Math.max(12, rect.top - menuHeight - offsetY);
    }

    const bounds = boundsRef?.current?.getBoundingClientRect?.();
    const minX = bounds ? bounds.left + 4 : 12;
    const maxX = bounds
      ? bounds.right - menuWidth - 4
      : window.innerWidth - menuWidth - 12;
    x = Math.max(minX, Math.min(x, Math.max(minX, maxX)));

    setPosition({ x, y });
  }, [align, anchorRef, boundsRef, offsetY]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const id = window.requestAnimationFrame(() => placeMenu());
    const onResize = () => placeMenu();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, placeMenu]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    let attached = false;
    const onDoc = (e) => {
      if (shouldIgnoreClose(e.target)) return;
      onClose();
    };
    const attach = () => {
      if (attached) return;
      attached = true;
      document.addEventListener("mousedown", onDoc);
    };
    const id = window.requestAnimationFrame(attach);
    return () => {
      window.cancelAnimationFrame(id);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [onClose, open, shouldIgnoreClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={className}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 10050,
        ...(style || {}),
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

export default TextFormatFloatingMenu;
